import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Attributes, AttributesRow, Card, DataStatus, Separator} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useGetManufacturerQuery,
  useGetStorageLocationByIdApiQuery,
  useGetTreeFolderPathQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getArticleDetailCardPrices} from '@dms/warehouse/shared';
import {ArticleDetailCardPrices} from '@dms/warehouse/shared-components';

import {composePath, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {ReceiveNoteItemDetails} from './types/ReceiveNoteItemDetails';

interface ArticleDetailCardProps extends TestIdProps {
  articleDetail: ReceiveNoteItemDetails['articleDetail'] | Nullish;
  currency: string;
  isDisabled: boolean;
  isExpanded: boolean;
  isRefreshingDetails: boolean;
  isArticleReplacement: boolean | Nullish;
  onRefresh: VoidFunction;
  onReplaceArticle: VoidFunction;
  warehouseName: string | undefined;
}

export function ArticleDetailCard(props: ArticleDetailCardProps) {
  const warehouseId = props.articleDetail?.warehouseId;

  const articleId = props.articleDetail?.id;

  const name = props.articleDetail?.name;

  const manufacturerNumber = props.articleDetail?.manufacturerNumber;

  const manufacturerId = props.articleDetail?.manufacturerId;

  const storageLocationId = props.articleDetail?.storageLocationId;

  const leafId = props.articleDetail?.treeFolder?.leafId;

  const {data: storageLocationDetail, isLoading: isStorageLocationLoading} =
    useGetStorageLocationByIdApiQuery(storageLocationId ? {storageLocationId} : skipToken);

  // Backwards compatibility: unmigrated articles have no `storageLocationId` yet,
  // so fall back to the legacy `storageLocation` string until BE migrates the data.
  const storageLocationName = storageLocationId
    ? storageLocationDetail?.location
    : props.articleDetail?.storageLocation;

  const {data: manufacturer, isLoading: isManufacturerLoading} = useGetManufacturerQuery(
    {manufacturerId: manufacturerId as string},
    {skip: isNilOrEmpty(manufacturerId)}
  );

  const {data: category, isLoading: isCategoryLoading} = useGetTreeFolderPathQuery(
    {leafId: leafId as string},
    {skip: isNilOrEmpty(leafId)}
  );

  const isLoading =
    props.isRefreshingDetails ||
    isManufacturerLoading ||
    isCategoryLoading ||
    isStorageLocationLoading;

  const isItemExisting = isNotNil(articleId);

  const prices = getArticleDetailCardPrices(props.articleDetail);

  const handleEditArticle = () => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId, id: articleId},
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.refresh'),
      leftIcon: 'navigation/refresh',
      onClick: props.onRefresh,
    },
    {
      type: 'button',
      variant: 'link',
      isDisabled: !props.isArticleReplacement,
      title: i18n.t('general.actions.replaceArticle'),
      leftIcon: 'action/autorenew',
      tooltip: i18n.t('entity.warehouse.labels.replaceArticleIsDisabled'),
      onClick: props.onReplaceArticle,
    },
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('entity.warehouse.actions.editArticle'),
      leftIcon: 'action/open_in_new',
      onClick: handleEditArticle,
    },
  ];

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: name,
    },
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      value: manufacturerNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: manufacturer?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.warehouse'),
      value: props.warehouseName,
    },
    {
      label: i18n.t('entity.warehouse.labels.storageLocation'),
      value: storageLocationName,
    },
    {
      label: i18n.t('entity.warehouse.labels.category'),
      value: category?.treeFolder?.pathName,
    },
  ];

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.articleDetail')}
      actions={isItemExisting ? actions : undefined}
      isClosedByDefault={props.isDisabled}
      isExpandable={props.isExpanded}
      data-testid={props['data-testid']}
    >
      <VStack>
        <DataStatus isLoading={isLoading} minHeight={70}>
          <Attributes rows={rows} data-testid={suffixTestId('articleDetailAttributes', props)} />
          <Separator spacing={3} />
          <ArticleDetailCardPrices prices={prices} currency={props.currency} />
        </DataStatus>
      </VStack>
    </Card>
  );
}
